import {
  type InteractiveAuthResponse,
  InteractiveAuthResponseEnum,
} from "@b2bportal/card-api";
import {
  type CartFulfillError,
  CartFulfillErrorModal,
  CartFulfillState,
} from "@checkout/types";
import { pollInteractivePaymentAuth } from "@hopper-b2b/api";
import {
  PollingResult,
  PollingType,
  endPollingPerformance,
  startPollingPerformance,
} from "@hopper-b2b/utilities";
import type { CartContext } from "../../context";
import { getFulfillCipherText } from "../selectors";

export const pollInteractivePaymentAuthService = async (
  context: CartContext,
  _event: unknown
): Promise<InteractiveAuthResponse | CartFulfillError> => {
  const fulFillCipherText = getFulfillCipherText({ context });
  const cartFulfillError: Omit<CartFulfillError, "type"> = {
    failedAt: CartFulfillState.pollInteractive,
  };

  try {
    if (!fulFillCipherText) {
      throw {
        ...cartFulfillError,
        type: CartFulfillErrorModal.NoCipherText,
      } as CartFulfillError;
    }

    const delayTimes = [1000];
    let shouldPollInteractivePaymentAuth = true;
    let index = 0;
    const startPerformance = startPollingPerformance();

    while (shouldPollInteractivePaymentAuth) {
      const currentDelayTime = delayTimes[index];
      if (index !== delayTimes.length - 1) {
        index++;
      }

      await new Promise((resolve) => setTimeout(resolve, currentDelayTime));

      const response: InteractiveAuthResponse =
        await pollInteractivePaymentAuth(fulFillCipherText.value);

      switch (response.InteractiveAuthResponse) {
        case InteractiveAuthResponseEnum.Failure: {
          shouldPollInteractivePaymentAuth = false;
          endPollingPerformance(
            startPerformance,
            PollingType.INTERACTIVEPAYMENTAUTH,
            PollingResult.FAILURE
          );

          if (response.errors.length > 0) {
            throw {
              ...cartFulfillError,
              type: CartFulfillErrorModal.FailedPolling,
              data: response.errors,
            } as CartFulfillError;
          } else {
            throw {
              ...cartFulfillError,
              type: CartFulfillErrorModal.FailedPollingNoErrorCode,
            } as CartFulfillError;
          }
        }
        case InteractiveAuthResponseEnum.Pending:
          continue;
        case InteractiveAuthResponseEnum.NotRequested: {
          endPollingPerformance(
            startPerformance,
            PollingType.INTERACTIVEPAYMENTAUTH,
            PollingResult.SUCCESS
          );

          shouldPollInteractivePaymentAuth = false;
          return response;
        }
        case InteractiveAuthResponseEnum.PendingInteractivePaymentAuth: {
          endPollingPerformance(
            startPerformance,
            PollingType.INTERACTIVEPAYMENTAUTH,
            PollingResult.CHALLENGE
          );

          shouldPollInteractivePaymentAuth = false;
          return response;
        }
      }
    }
  } catch (e) {
    throw {
      ...cartFulfillError,
      type: CartFulfillErrorModal.FailedInteractiveAuth,
      data: e,
    };
  }
};
